import React, { useEffect } from 'react';
import { useApptekster } from '../../contexts/ApptekstContext';
import { useApi } from '../../hooks/useApi';
import { useSoknad } from '../../hooks/useSoknad';
import {
    AktivSakStatus,
    DineSkiltSakStatus,
    hentSoknadsStatuser,
    PersonligKjennemerkeStatus,
    SakStatus,
} from '../../utils/api';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { Tekster } from '../../types/apptekster';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import {
    SVVNavigationCard2,
    SVVNavigationCardList,
} from '@komponentkassen/svv-navigation-card';

export const Statusoversikt: React.FC = () => {
    const t = useApptekster();
    const { hent, laster, data } = useApi(hentSoknadsStatuser, {
        caching: false,
    });
    useEffect(() => {
        hent();
    }, [hent]);
    const { aktiveSoknader, dineSkiltSoknader } = useSoknad(data);

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="/dinside/dinekjoretoy/skilt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.skilt')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.skilt')}
                </SVVBreadcrumbLink>
                <h1>{t('personlig-kjennemerke.delt.personlig-skilt')}</h1>
                <p className="svv-ingress spacing-bottom--medium">
                    {t('personlig-kjennemerke.statusoversikt.ingress')}
                </p>
                <div className="spacing-bottom--medium">
                    <SVVNavigationCardList ariaLabel="Undermeny">
                        <SVVNavigationCard2
                            url={'#/soknad'}
                            heading={t('personlig-kjennemerke.snarvei.sok')}
                        />
                    </SVVNavigationCardList>
                </div>

                <div aria-live="polite">
                    {laster && (
                        <SVVSpinner size="large">
                            {t('personlig-kjennemerke.delt.laster')}
                        </SVVSpinner>
                    )}
                </div>
                {!laster && !!aktiveSoknader.length && (
                    <div>
                        <h2 className="spacing-bottom--small">
                            {t(
                                'personlig-kjennemerke.statusoversikt.aktive-soknader',
                            )}
                        </h2>

                        <SVVNavigationCardList ariaLabel="Undermeny">
                            {aktiveSoknader?.map(
                                (soknad: PersonligKjennemerkeStatus) => (
                                    <SVVNavigationCard2
                                        key={
                                            soknad.rettighetId ||
                                            soknad.soknadId
                                        }
                                        url={`#status/${
                                            soknad.rettighetId ||
                                            soknad.soknadId
                                        }`}
                                        heading={`${t(
                                            'personlig-kjennemerke.delt.personlig-skilt',
                                        )}: ${soknad.tegnkombinasjon}`}
                                        ingress={`${t(
                                            'personlig-kjennemerke.delt.status',
                                        )}
                                                ${t(
                                                    Statustekst(
                                                        soknad.sakStatus,
                                                    ) as keyof Tekster,
                                                )}`}
                                    />
                                ),
                            )}
                        </SVVNavigationCardList>
                    </div>
                )}
            </SVVLayoutBanner>

            {!!dineSkiltSoknader.length && (
                <SVVLayoutBanner>
                    <h2 className="spacing-bottom--small">
                        {t(
                            'personlig-kjennemerke.statusoversikt.dine-personlige-skilt',
                        )}
                    </h2>
                    <SVVNavigationCardList ariaLabel="Undermeny">
                        {dineSkiltSoknader?.map(
                            (skilt: PersonligKjennemerkeStatus) => (
                                <SVVNavigationCard2
                                    key={skilt.rettighetId}
                                    url={`#status/${skilt.rettighetId}`}
                                    heading={`${t(
                                        'personlig-kjennemerke.delt.personlig-skilt',
                                    )}: ${skilt.tegnkombinasjon}`}
                                    ingress={`${
                                        skilt.sakStatus === 'KNYTTET'
                                            ? t(
                                                  Statustekst(
                                                      skilt.sakStatus,
                                                  ) as keyof Tekster,
                                              ) +
                                              ' ' +
                                              skilt.aliasFor
                                            : t(
                                                  Statustekst(
                                                      skilt.sakStatus,
                                                  ) as keyof Tekster,
                                              )
                                    }`}
                                />
                            ),
                        )}
                    </SVVNavigationCardList>
                </SVVLayoutBanner>
            )}
        </>
    );
};

function Statustekst(status: SakStatus) {
    switch (status) {
        case AktivSakStatus.MOTTATT:
        case AktivSakStatus.UNDER_BEHANDLING:
            return 'personlig-kjennemerke.statusoversikt.status.sendt';
        case AktivSakStatus.INNVILGET:
            return 'personlig-kjennemerke.statusoversikt.status.godkjent';
        case AktivSakStatus.BETALT:
            return 'personlig-kjennemerke.statusoversikt.status.betalt';
        case AktivSakStatus.SKILT_BESTILT:
        case AktivSakStatus.SKILT_BESTILT_BRUKER:
            return 'personlig-kjennemerke.statusoversikt.status.bestilt';
        case AktivSakStatus.SKILT_MOTTATT:
            return 'personlig-kjennemerke.statusoversikt.status.hent';
        case DineSkiltSakStatus.SKILT_UTLEVERT:
            return 'personlig-kjennemerke.statusoversikt.status.utlevert';
        case DineSkiltSakStatus.KNYTTET:
            return 'personlig-kjennemerke.statusoversikt.status.knyttet';
        default:
            return '';
    }
}
